/**
 * video/element.ts — the one and only video element of the application.
 *
 * Task 14. `spec.md` «Видео»: «mp4 из канала играет в одном видеоэлементе внутри вкладки, с
 * перемоткой по диапазонам и кадром-превью. Этот элемент — единственное разрешённое место
 * создания видеоэлемента; правило проверки кода это охраняет». This module is that place: the
 * `fdm/no-new-video` rule names this path as its only exception (`eslint.config.js`), and
 * `test/video-element.test.ts` reads the sources of `apps/web/src` for the same thing. That is
 * why no comment in `apps/web/src` spells out a construction form.
 *
 * ## Why one element, and why made here and not by React
 *
 * The same reasons as the audio element's (`engine/element.ts`), one level up. A browser puts
 * its autoplay rule on each element, and a tap lifts it for that element only: an element per
 * clip is locked again on every clip. Starting a clip pauses the music and starting the music
 * pauses the clip (`engine/video.ts`), and that interlock is wired to THIS element — a second
 * one made by some component would be invisible to it, and two sounds would play at once. And
 * leaving the «Видео» tab has to silence the clip: a component-owned element unmounts still
 * holding its decoder, and on Android goes on sounding from nowhere.
 *
 * So the element is made once, when this module is first evaluated, and never replaced. It is
 * NOT in the document until a clip is shown: `engine/video.ts` puts it into the player area of
 * the tab and takes it out when the tab goes. An element outside the document plays the same,
 * which is what lets a tap in «Поиск» start a clip before the tab exists.
 *
 * ## Its settings
 *
 * - `controls` — the phone's own play button, seek bar and full-screen: «с перемоткой», and the
 *   bar drags over byte ranges the server answers with `206` (`apps/api/src/routes/stream.ts`).
 * - `controlslist="nodownload"` — Chrome's own menu has no «Скачать». `spec.md` «Доступ»: there
 *   is no download button, and the phone's menu must not add one.
 * - `playsInline` — iOS would otherwise take every clip full-screen on its first frame.
 * - `preload = 'none'`: nothing is fetched until play. The poster is Telegram's frame, served by
 *   us, so a clip is seen before a byte of it is paid for.
 * - `crossOrigin` is NOT set, for the audio element's reason: the bytes are same-origin, and
 *   opting into CORS would split the browser's cache.
 *
 * Everything else takes a {@link ClipSurface}, so a test drives the engine with a recording
 * fake (`test/helpers/fake-video.ts`); {@link clipSurface} is the production one.
 */

/** The part of `HTMLVideoElement` the engine touches. */
export interface ClipElementLike {
  src: string;
  poster: string;
  readonly paused: boolean;
  readonly error: { readonly code: number } | null;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  removeAttribute(name: string): void;
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

/**
 * The element twice: as a player, and as the thing put on the page. In the browser both are the
 * one element; a test's fake records the first and hands a plain node as the second.
 */
export interface ClipSurface {
  readonly element: ClipElementLike;
  readonly node: HTMLElement;
}

function createSingleton(): HTMLVideoElement {
  const element = document.createElement('video');
  element.preload = 'none';
  element.playsInline = true;
  element.controls = true;
  element.setAttribute('controlslist', 'nodownload');
  element.className = 'videoClip';
  return element;
}

/** Created when this module is first evaluated. Never replaced. */
export const clip: HTMLVideoElement = createSingleton();

/** The production surface: the one element, as both halves. */
export const clipSurface: ClipSurface = { element: clip, node: clip };
